import useApi from '../../../utils/axios-pettitions';
import { setIsAuthenticated } from '../../../contexts/slices/config';
import { clearFilteredOrders, clearOrder, clearSelectedOrders } from '../../../contexts/slices/orders';
import { useAppDispatch } from '../../../contexts/hooks';
import { PermissionsCampus, UserState } from '../../../types/types.user';
import { login as loginRdx, setLoading, putPermissionsCampus, logOut as logOutRdx } from '../../../contexts/slices/auth';
import { setLoadingCnf, clearFullConfig } from '../../../contexts/slices/config';
import { LoginStruct } from '../../../types/types.user';
import notify from 'devextreme/ui/notify';
import { clearAllNotifications } from '../../../contexts/slices/notifications';
import { clearQuotation } from '../../../contexts/slices/quotations';
import { setDashboards } from '../../../contexts/slices/dashboards';
import { sessionManager } from '../../../utils/session-manager';

/// Bloqueo temporal por demasiados intentos fallidos de inicio de sesión.
export const LOGIN_BLOCKED_CODE = 222;
/// Acceso bloqueado de forma permanente (IP bloqueada por el administrador).
export const ACCESS_BLOCKED_CODE = 203;

export interface LoginResult {
  code: number;
  message: string;
  /// Segundos que faltan para poder reintentar. Sólo aplica al código 222.
  blockedSeconds: number;
}

const Index = () => {
  const { runApi } = useApi();
  const dispatch = useAppDispatch();

  // Devuelve el resultado del login para que el formulario pueda reaccionar
  // (resetear el captcha en intentos fallidos, mostrar el bloqueo por demasiados
  // intentos). runApi nunca lanza: los errores también llegan como HTTPResponse.
  const login = async (loginData: LoginStruct): Promise<LoginResult> => {
    dispatch(setLoading(true));
    return runApi('Authentication/Login', 'POST', null, loginData)
      .then((res) => {
        if (res.internalCode < 200) {
          notify(
            {
              message: res.message,
              type: 'success',
              displayTime: 10000,
              animation: {
                show: {
                  type: 'fade',
                  duration: 400,
                  from: 0,
                  to: 1,
                },
                hide: { type: 'fade', duration: 40, to: 0 },
              },
            },
            {
              position: 'bottom right',
              direction: 'up-stack',
            }
          );
          if (res.internalCode === 102) {
            dispatch(putPermissionsCampus(res.data as PermissionsCampus[]));
          }
          if (res.internalCode === 100) {
            const data: UserState = res.data as UserState;
            dispatch(loginRdx(data));
            dispatch(setIsAuthenticated(true));

            // Inicializar el sistema de sesión robusto
            const sessionTimeoutMinutes = 10; // Valor por defecto
            sessionManager.initializeSession(sessionTimeoutMinutes, data.User?.Us_Id?.toString());
          }
        }

        // 222: bloqueo temporal por demasiados intentos fallidos. El backend envía
        // los segundos que faltan para poder reintentar.
        const blockedSeconds = res.internalCode === LOGIN_BLOCKED_CODE ? (res.data as { SegundosRestantes?: number })?.SegundosRestantes ?? 0 : 0;

        return { code: res.internalCode, message: res.message, blockedSeconds };
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const logOut = async () => {
    dispatch(setLoadingCnf(true));
    
    try {
      // Primero cerrar sesión en el servidor
      const res = await runApi('Authentication/LogOut', 'POST');
      
      if (res.internalCode < 200) {
        notify(
          {
            message: res.message,
            type: 'success',
            displayTime: 10000,
            animation: {
              show: {
                type: 'fade',
                duration: 400,
                from: 0,
                to: 1,
              },
              hide: { type: 'fade', duration: 40, to: 0 },
            },
          },
          {
            position: 'bottom right',
            direction: 'up-stack',
          }
        );
        
        if (res.internalCode === 103) {
          // Limpiar estado de Redux
          dispatch(logOutRdx());
          dispatch(setIsAuthenticated(false));
          dispatch(clearFullConfig());
          dispatch(clearOrder());
          dispatch(clearQuotation());
          dispatch(clearSelectedOrders());
          dispatch(clearAllNotifications());
          dispatch(setDashboards([]));
          dispatch(clearFilteredOrders());
          
          // Cerrar sesión en el sistema de gestión de sesión
          await sessionManager.logout();
        }
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Aun si falla el logout en el servidor, limpiar localmente
      dispatch(logOutRdx());
      dispatch(setIsAuthenticated(false));
      dispatch(clearOrder());
      dispatch(clearQuotation());
      dispatch(clearSelectedOrders());
      dispatch(clearAllNotifications());
      dispatch(setDashboards([]));
      dispatch(clearFilteredOrders());
      
      await sessionManager.logout();
    } finally {
      dispatch(setLoadingCnf(false));
    }
  };

  return { login, logOut };
};

export default Index;
